import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WorkIcon from '@mui/icons-material/Work';
import ScheduleIcon from '@mui/icons-material/Schedule';
import '../styles/Dashboard.css';

interface Stats {
  drivers: {
    total: number;
    online: number;
    offline: number;
  };
  jobs: {
    total: number;
    pending: number;
    active: number;
    completed: number;
  };
  offers: {
    total: number;
    active: number;
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
      setError('');
    } catch (err: any) {
      setError('Failed to load statistics');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-box">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>System Overview & Statistics</p>
      </div>

      {/* Driver Stats */}
      <div className="stats-section">
        <h2>Drivers</h2>
        <div className="stats-grid">
          <div className="stat-card clickable" onClick={() => navigate('/drivers')}>
            <div className="stat-icon driver-total">
              <DirectionsCarIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.drivers.total || 0}</h3>
              <p>Total Drivers</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate('/drivers?filter=online')}>
            <div className="stat-icon driver-online">
              <CheckCircleIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.drivers.online || 0}</h3>
              <p>Online Now</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate('/drivers?filter=offline')}>
            <div className="stat-icon driver-offline">
              <CancelIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.drivers.offline || 0}</h3>
              <p>Offline</p>
            </div>
          </div>
        </div>
      </div>

      {/* Job Stats */}
      <div className="stats-section">
        <h2>Jobs</h2>
        <div className="stats-grid">
          <div className="stat-card clickable" onClick={() => navigate('/jobs')}>
            <div className="stat-icon job-total">
              <AssignmentIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.jobs.total || 0}</h3>
              <p>Total Jobs</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate('/jobs?status=pending')}>
            <div className="stat-icon job-pending">
              <HourglassEmptyIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.jobs.pending || 0}</h3>
              <p>Pending</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate('/jobs?status=in_progress')}>
            <div className="stat-icon job-active">
              <SyncIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.jobs.active || 0}</h3>
              <p>Active</p>
            </div>
          </div>

          <div className="stat-card clickable" onClick={() => navigate('/jobs?status=completed')}>
            <div className="stat-icon job-completed">
              <TaskAltIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.jobs.completed || 0}</h3>
              <p>Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Offer Stats */}
      <div className="stats-section">
        <h2>Job Offers</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon offer-total">
              <WorkIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.offers.total || 0}</h3>
              <p>Total Offers</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon offer-active">
              <ScheduleIcon />
            </div>
            <div className="stat-content">
              <h3>{stats?.offers.active || 0}</h3>
              <p>Active Offers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
